// Game Asset Bootstrapper - централизованная загрузка игровых ассетов
import { logger } from '@/engine/logging';
import { ServiceRegistry, ServiceKeys } from '@/engine/registry';
import { GameAssetService } from './GameAssetService';
import { TextureFactory } from '../graphics/TextureFactory';
import { eventBus } from '@/engine/events/EventBus';
import { loadingProgressService } from '@/engine/loading/LoadingProgressService';
import { initializeGameDatabases } from '@/game/database';

export class GameAssetBootstrapper {
  private static isInitialized = false;

  /**
   * Загрузка и инициализация всех игровых ассетов
   */
  public static async loadAssetsAndInitializeFactories(): Promise<void> {
    if (this.isInitialized) {
      logger.warn('⚠️ Game assets already loaded', { source: 'GameAssetBootstrapper' });
      return;
    }

    try {
      logger.info('🎨 Loading game assets and initializing factories...', {
        source: 'GameAssetBootstrapper',
      });

      // 1. Инициализация игровых баз данных (в правильном порядке)
      loadingProgressService.updateProgress(5, 'Initializing Game Databases...');
      initializeGameDatabases();

      // 2. Получаем AssetManager из ServiceRegistry
      loadingProgressService.updateProgress(10, 'Getting Asset Manager...');
      const assetManager = ServiceRegistry.get<import('@/engine/assets/AssetManager').AssetManager>(
        ServiceKeys.AssetManager
      );

      if (!assetManager) {
        throw new Error('AssetManager not found in ServiceRegistry');
      }

      // 3. Создаем и регистрируем GameAssetService
      loadingProgressService.updateProgress(20, 'Initializing Asset Service...');
      const gameAssetService = new GameAssetService(assetManager.loader);
      await gameAssetService.registerAssets();
      ServiceRegistry.register('gameAssetService', gameAssetService);

      // 4. Инициализируем TextureFactory
      loadingProgressService.updateProgress(30, 'Setting up Texture Factory...');
      const textureFactory = TextureFactory.getInstance();
      textureFactory.initialize();
      ServiceRegistry.register('textureFactory', textureFactory);

      // 5. Загружаем игровые текстуры
      loadingProgressService.updateProgress(40, 'Loading Game Textures...');
      await gameAssetService.loadTextures();

      loadingProgressService.updateProgress(50, 'Finalizing Assets...');

      // Эмитируем событие о загрузке ассетов
      eventBus.emit('game-assets-loaded');
      logger.info('✅ Game assets loaded and event emitted', { source: 'GameAssetBootstrapper' });

      this.isInitialized = true;
    } catch (error) {
      logger.error('❌ Failed to load game assets', error as Error, {
        source: 'GameAssetBootstrapper',
      });
      throw error;
    }
  }

  /**
   * Проверка статуса инициализации
   */
  public static get isAssetsLoaded(): boolean {
    return this.isInitialized;
  }

  /**
   * Сброс статуса (для HMR)
   */
  public static reset(): void {
    this.isInitialized = false;
    logger.info('🔄 GameAssetBootstrapper reset', { source: 'GameAssetBootstrapper' });
  }
}
